<template>
    <!-- Wrap your app content -->
    <slot />
  </template>
  
  <script setup>
  import { ref, onMounted, provide } from 'vue';
  import { useRoute } from 'vue-router';
  import Shepherd from 'shepherd.js';
  import 'shepherd.js/dist/css/shepherd.css';
  
  const tour = ref(null);
  const route = useRoute();
  
  const startTour = () => {
    tour.value && tour.value.start();
  };
  provide('startTour', startTour);
  
  onMounted(() => {
    tour.value = new Shepherd.Tour({
      useModalOverlay: true,
      defaultStepOptions: {
        classes: 'shadow-md bg-purple-dark',
        scrollTo: true,
        cancelIcon: {
            enabled: true,
            label: '✖'  // or any icon/text you prefer
        }
      },
    });
  
    tour.value.addStep({
    id: 'start',
    text: 'Welcome to Pairboard! Let’s get you acquainted with our platform. If you need to pause the tour, you can always restart it later by clicking the "Tutorial" button on the left.',
    attachTo: { element: '#tutorial-button', on: 'right' },
    buttons: [
        { text: 'Cancel', classes: 'secondary', action: () => tour.value.cancel() },
        { text: 'Next', classes: 'primary', action: () => tour.value.next() },
    ]
    });
    tour.value.addStep({
    id: 'light-dark',
    text: 'Pairboard offers both light and dark modes to suit your preferences. Toggle between them for the best viewing experience.',
    attachTo: { element: '.theme-toggle', on: 'bottom' },
    buttons: [
        { text: 'Back', classes: 'secondary', action: () => tour.value.back() },
        { text: 'Next', classes: 'primary', action: () => tour.value.next() },
    ]
    });
    tour.value.addStep({
    id: 'create-project',
    text: 'Begin by creating a project. Projects help you organize your goals and tasks all in one place.',
    attachTo: { element: '#add-project-li', on: 'right' },
    buttons: [
        { text: 'Back', classes: 'secondary', action: () => tour.value.back() },
        { text: 'Next', classes: 'primary', action: () => tour.value.next() },
    ]
    });
    tour.value.addStep({
    id: 'add-email',
    text: 'Connect your email account to Pairboard. This feature lets you view, draft, and even get AI assistance with your emails.',
    attachTo: { element: '#add-email-li', on: 'right' },
    buttons: [
        { text: 'Back', classes: 'secondary', action: () => tour.value.back() },
        { text: 'Next', classes: 'primary', action: () => tour.value.next() },
    ]
    });
    tour.value.addStep({
    id: 'upload-info',
    text: 'Upload documents to Pairboard so our AI can provide more tailored and effective responses.',
    attachTo: { element: '#upload-info-li', on: 'right' },
    buttons: [
        { text: 'Back', classes: 'secondary', action: () => tour.value.back() },
        { text: 'Next', classes: 'primary', action: () => tour.value.next() },
    ]
    });
    tour.value.addStep({
    id: 'connections',
    text: 'Enhance your experience with Connections! Manage emails, create documents, and more – all without leaving Pairboard.',
    attachTo: { element: '#connection-li', on: 'right' },
    buttons: [
        { text: 'Back', classes: 'secondary', action: () => tour.value.back() },
        { text: 'Next', classes: 'primary', action: () => tour.value.next() },
    ]
    });
    tour.value.addStep({
    id: 'feedback',
    text: 'Pairboard is a work in progress! If you run into any issues or have suggestions to improve the experience, we’d love to hear from you ♥.',
    attachTo: { element: '#feedback-li', on: 'right' },
    buttons: [
        { text: 'Back', classes: 'secondary', action: () => tour.value.back() },
        { text: 'Finish', classes: 'primary', action: () => tour.value.next() },
    ]
    });

    // If not on login and cookie isn't set, set cookie immediately and start the tour.
    if (route.name !== 'login' && !document.cookie.includes("tutorialDone=true")) {
      document.cookie = "tutorialDone=true; max-age=31536000; path=/";
      startTour();
    }
  });
  </script>
  
  
<style>
/* Shepherd container & content */
.shepherd-container {
  font-family: var(--body-font);
}

.shepherd-content {
  background-color: var(--Theme-Color-Secondary-Black);
  color: var(--white-color);
  border: 1px solid var(--Border-Color-Primary);
  padding: 1rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.5);
}

/* Title and text */
.shepherd-title {
  font-family: var(--heading-font);
  font-size: 1.25rem;
  margin-bottom: 0.5rem;
}

.shepherd-text {
  font-size: 1rem;
  line-height: 1.5;
  margin-bottom: 1rem;
  color: var(--white-color);
}

/* Buttons */
.shepherd-button.primary {
  background-color: var(--primary-color);
  background: var(--primary-color);
  border: none;
  color: var(--white-color);
  padding: 0.5rem 1rem;
  border-radius: 4px;
  cursor: pointer;
  transition: transform 0.2s ease;
}

.shepherd-button.primary:not(:disabled):hover{
  background-color: var(--primary-color);
  background: var(--primary-color);
  transform: translateY(-2px);
  
}
/* Buttons */
.shepherd-button.secondary {
    border: 1px solid var(--primary-color);
    background-color: transparent;
    color: var(--white-color);
    padding: 0.5rem 1rem;
    border-radius: 4px;
    cursor: pointer;
    transition: transform 0.2s ease;
    color: var(--primary-color);

}

.shepherd-button.secondary:not(:disabled):hover{
  border: 1px solid var(--primary-color);
  background: transparent;
  color: var(--primary-color);
  transform: translateY(-2px);
}

.shepherd-button:active {
  transform: translateY(0);
}

.shepherd-button[disabled] {
  background-color: var(--gray-color-2c);
  cursor: not-allowed;
}

/* Cancel Icon */
.shepherd-cancel-icon {
  color: var(--dark-colo-a0);
  cursor: pointer;
}

/* Arrow */
.shepherd-arrow::before {
  background-color: var(--Border-Color-Primary);
}
.shepherd-cancel-icon{
    color: var(--white-color)
}
.shepherd-cancel-icon,
.shepherd-cancel-icon:hover,
.shepherd-cancel-icon:active,
.shepherd-cancel-icon:focus {
  all: unset !important;
  cursor: pointer !important;
}

.shepherd-header{
    padding: 0;
}
</style>